import cloneDeep from "lodash/cloneDeep";

/*

Storage paradigm:

Everything goes into `localStorage.PhaserfrootStoredData`.
This contains an array of objects, each representing a game.
Game data is an object: `{ name, data }`,
where `name` is the pathname where the game ran,
and `data` is the object on which the user sets keys.

This keeps all the Phaserfroot data together,
and allows us control to the level of clearing an entire game's data.

If storage quota is exceeded, we discard game data oldest first,
until either we succeed or we abort the process, preserving the former state
of `PhaserfrootStoredData`.

*/

const PhaserfrootStoredData = "PhaserfrootStoredData";

class GameStorageProxy {
  /**
   * @param {string} name - storage key
   * @param {string} data - parsed data
   */
  constructor(name, data) {
    this.name = name;
    this.data = data;
  }

  clear() {
    this.data = {};
  }

  getItem(key) {
    return this.data[key];
  }

  removeItem(key) {
    delete this.data[key];
  }

  setItem(key, value) {
    this.data[key] = value;
  }
}

/**
 * A stand-in for local storage, allowing users to save data if there is space.
 * Data is stored in the PhaserfrootStoredData namespace,
 * in proxies named (by default) after the pathname of the current site.
 * Override `getName()` to specify a different name, e.g. in the Editor Preview.
 */
const LocalStorageProvider = {

  /** Game proxy used by the running game. */
  _currentGame: undefined,

  /** List of proxies, each concerning an individual game's stored data. */
  _games: [],

  /** Whether `this._games` has been loaded from storage this session. */
  _loaded: false,

  /**
   * Maximum number of characters that will be stored.
   * This allows us to prevent Phaserfroot from using the entire storage quota
   * for the domain. This is necessary in the Gamefroot Editor,
   * which has its own uses for storage.
   * Overwrite this value before booting the game.
   */
  quota: 5000000,

  clear: function () {
    if (!this._loaded) {
      this.loadGames();
    }
    // Duplicate storage for tentative operation.
    const games = cloneDeep(this._games);
    const game = games[games.length - 1];

    this.game.clear();

    this.syncData(game, games);
  },

  getItem: function (key) {
    if (!this._loaded) {
      this.loadGames();
    }
    return this._currentGame.data[key];
  },

  /**
   * Return the name under which to store the current game.
   * Override this in the game config to change the storage key of the game.
   */
  getName: function () {
    return location.pathname;
  },

  /** Populate data from storage. */
  loadGames: function () {
    const games = this._games;
    const data = localStorage.getItem(PhaserfrootStoredData);
    if (data) {
      try {
        const gameData = JSON.parse(data);
        gameData.forEach(game => {
          games.push(
            new GameStorageProxy(game.name, game.data));
;        });
      } catch (error) {
        console.error("Error loading stored data:", error);
      }
    }

    // Set the current game and make it the most recent.
    const name = this.getName();
    let currentGame = games.find(game => game.name === name);
    if (currentGame) {
      games.splice(games.indexOf(currentGame), 1);
    } else {
      currentGame = new GameStorageProxy(name, {});
    }
    games.push(currentGame);
    this._currentGame = currentGame;
    this._loaded = true;
  },

  removeItem: function (key) {
    if (!this._loaded) {
      this.loadGames();
    }
    // Duplicate storage for tentative operation.
    const games = cloneDeep(this._games);
    const game = games[games.length - 1];

    // Set the actual data.
    game.removeItem(key);

    this.syncData(game, games);
  },
  
  setItem: function (key, value) {
    if (!this._loaded) {
      this.loadGames();
    }
    // Duplicate storage for tentative operation.
    const games = cloneDeep(this._games);
    const game = games[games.length - 1];

    // Set the actual data.
    game.setItem(key, value);

    this.syncData(game, games);
  },

  syncData: function (currentGame, games) {
    // Will the data fit in storage?
    let success = false;
    let lastError;
    const quotaExceededMessage = "The sum of game data on this domain exceeds the configured quota.";

    if (games.length > 1) {
      // Try to fit it into custom quota.
      let serial = JSON.stringify(games);
      let withinQuota = serial.length <= this.quota;
      while (
        games.length > 1 &&
        !withinQuota
      ) {
        // Pop the oldest game out of storage until quota is met or we fail.
        games.shift();
        serial = JSON.stringify(games);
        withinQuota = serial.length <= this.quota;
      }
      if (withinQuota) {
        // Try to fit it into storage.
        while (!success && (games.length > 0)) {
          try {
            localStorage.setItem(PhaserfrootStoredData, serial);
            success = true;
          } catch (error) {
            lastError = error;
            if (games.length = 1) {
              // We just can't save this data.
              break;
            }
            // Pop the oldest game out of storage and try again.
            games.shift();
            serial = JSON.stringify(games);
          }
        }
      } else {
        lastError = new Error(quotaExceededMessage);
      }
    } else {
      // There must be just a single game.
      const serial = JSON.stringify(games);
      if (serial.length > this.quota) {
        lastError = new Error(quotaExceededMessage);
      } else {
        try {
          localStorage.setItem(PhaserfrootStoredData, serial);
          success = true;
        } catch (error) {
          lastError = error;
        }
      }
    }
    if (success) {
      // Overwrite proxy list.
      this._games = games;
      this._currentGame = currentGame;
    } else {
      console.error("Could not save data: localStorage is full!", lastError);
    }
  },

  // There is no `key` because we don't use it.
};

export default LocalStorageProvider;
